<!--
	@name app-account-settings
	@description Account Page
	@date 2020/04/24
	@license no license
	@copywrite Answers In Retirement Limited
-->

<template>
	<div :component="$options.name" class="pa-3">
		<v-sheet class="pa-6 mb-9 rounded background-gradient">
			<v-row no-gutters>
				<v-col class="shrink align-self-center">
					<div class="align-self-center nowrap headline white--text">My Account</div>
				</v-col>
				<v-col class="grow text-right">
					<v-tooltip v-if="allowedAccountSettings.includes('long-life-token')" bottom>
						<template #activator="{ on }">
							<v-btn large text color="white" @click="openTokenCreationDialog" v-on="on">
								<v-icon large> mdi-key-plus </v-icon>
							</v-btn>
						</template>
						<span>Create Token</span>
					</v-tooltip>
					<v-tooltip bottom>
						<template #activator="{ on }">
							<v-btn large text color="white" @click="openAppLoginDialog" v-on="on">
								<v-icon large> mdi-cellphone-link </v-icon>
							</v-btn>
						</template>
						<span>Log in to the Air app</span>
					</v-tooltip>
					<v-tooltip bottom>
						<template #activator="{ on }">
							<v-btn large text color="white" to="/account" v-on="on">
								<v-icon large> mdi-backburger </v-icon>
							</v-btn>
						</template>
						<span>Back to Dashboard</span>
					</v-tooltip>
				</v-col>
			</v-row>
		</v-sheet>

		<v-row>
			<v-col class="py-0" cols="12" md="8">
				<div class="border-effect border-effect--lg ml-6 mb-10">
					<p class="text-h5 mb-2 font-weight-regular">View and edit the details we store about you from <span class="font-weight-bold">the 'Details' tab</span></p>
					<p class="text-body-1 mb-0">
						The 'Security' tab is used to update your critical account information and protect your account with 2FA. Use the 'Credentials' tab to connect your account
						with our many integrated partners, as well as to store your lender portal login details. The 'Preferences' tab gives you complete control over how we'll
						contact you about our services and special offers.
					</p>
				</div>

				<!-- Tabs -->
				<common-base-tab-view :tabs="tabs" :loading="!loaded" router-view dark />
			</v-col>

			<v-col class="py-0" cols="6" md="4">
				<common-link-shortcuts v-if="!isAdmin" :items="shortcutAccount.list" />

				<common-promotion-advisor-advert />
			</v-col>
		</v-row>

		<!-- Token Creation Dialog -->
		<dynamic-form-dialog ref="tokenFormDialog" @dynamic-form-submit="submitToken">
			<template #header>
				<p class="text-body-2 mb-3">
					A long life access token can be used to authenticate a third party system (such as a CRM) to make API requests on your behalf.
					<span class="info--text"
						><strong>Please note: long life access tokens do not expire automatically.</strong> Please ensure that you revoke access for a long life access token if the
						token is no longer required.</span
					>
				</p>
			</template>
		</dynamic-form-dialog>

		<!-- Token Response Dialog -->
		<common-dialog ref="tokenResponseDialog" :max-width="800" color="success">
			<template #header> Token Created Successfully </template>
			<template #body>
				<div v-if="tokenResponse">
					<p class="text-body-2 mb-1">Your long life access token has been created successfully.</p>
					<p class="text-body-2 font-weight-bold mb-6">
						Click the <v-icon size="16"> mdi-content-copy </v-icon> button to copy to your clipboard. Please save this token securely, as it will not be shown again.
					</p>

					<v-sheet class="blue-grey lighten-5 rounded mb-5" style="overflow: hidden">
						<v-row no-gutters class="blue-grey lighten-2 py-3 px-5">
							<v-col class="text-body-2 font-weight-bold white--text grow align-content-center pa-0"> Access Token </v-col>
							<v-col class="shrink align-content-center pa-0">
								<v-tooltip bottom>
									<template #activator="{ on }">
										<v-icon color="white" size="24" @click="copyToClipboard(tokenResponse.accessToken)" v-on="on"> mdi-content-copy </v-icon>
									</template>
									<span>Copy to clipboard</span>
								</v-tooltip>
							</v-col>
						</v-row>
						<p class="text-caption plain text-wrap py-3 px-5 mb-0" style="word-break: break-word">
							{{ tokenResponse.accessToken }}
						</p>
					</v-sheet>
				</div>
			</template>
		</common-dialog>

		<!-- App Login Dialog -->
		<app-login-dialog ref="appLoginDialog" />
	</div>
</template>

<script>
	import Vue from 'vue';
	import { mapActions, mapGetters, mapState } from 'vuex';
	import CommonBaseTabView from '@/component/common/base/tab-view';
	import CommonLinkShortcuts from '@/component/common/link/shortcuts';
	import CommonPromotionAdvisorAdvert from '@/component/common/promotion/advisor-advert';
	import DynamicFormDialog from '@/component/common/dialog/dynamic-form-dialog';
	import CommonDialog from '@/component/common/dialog';
	import AppLoginDialog from '@/component/app/account-settings/dialog/app-login';
	import { ElementTools, EventBus } from '@/utils';

	export default {
		name: 'app-account-settings',

		components: {
			CommonBaseTabView,
			CommonLinkShortcuts,
			CommonPromotionAdvisorAdvert,
			DynamicFormDialog,
			CommonDialog,
			AppLoginDialog
		},

		data() {
			return {
				loaded: false,
				tokenResponse: null,
				tabs: [
					{ name: 'settings.details', title: 'Details', alerts: 0, path: '/settings/details' },
					{ name: 'settings.security', title: 'Security', alerts: 0, path: '/settings/security' },
					{ name: 'settings.credentials', title: 'Credentials', alerts: 0, path: '/settings/credentials' },
					{ name: 'settings.preferences', title: 'Preferences', alerts: 0, path: '/settings/preferences' }
				]
			};
		},

		computed: {
			...mapState('Account', ['user']),
			...mapState('CmsSite', ['site']),
			...mapGetters('Account', ['isAdmin']),
			...mapGetters('CmsShortcut', ['shortcutAccount']),
			...mapGetters('CmsData', ['data']),
			...mapGetters('CmsSite', ['allowedAccountSettings']),

			hasBanner() {
				return this.site.adverts?.login;
			}
		},

		watch: {
			'$route.name': {
				handler(name) {
					if (name === 'settings') this.$router.push('/settings/details');
				},
				deep: true,
				immediate: true
			}
		},

		created() {
			this.loadUserDetails(this.user.id).then(() => (this.loaded = true));
			this.loadShortcut('account');
			if (this.isAdmin) this.tabs = this.tabs.filter((tab) => tab.name !== 'settings.credentials');
		},

		methods: {
			...mapActions('CmsForm', ['loadForm']),
			...mapActions('Account', ['loadUserDetails']),
			...mapActions('CmsShortcut', ['loadShortcut']),
			...mapActions('AccountUserLongLifeAccessToken', ['createUserLongLifeAccessToken']),

			/**
			 * @name openAppLoginDialog
			 * @description Open the dialog that hands this session over to the mobile app
			 */
			openAppLoginDialog() {
				this.$refs.appLoginDialog.open();
			},

			/**
			 * @name openTokenCreationDialog
			 * @description Open the token creation dialog with dynamic form
			 */
			openTokenCreationDialog() {
				this.loadForm('long_life_access_token').then((form) => {
					this.$refs.tokenFormDialog.open({
						formSchema: form.data.value,
						formData: {},
						submitButtonText: 'Create Token',
						type: 'add',
						title: 'Create Long Life Access Token'
					});
				});
			},

			/**
			 * @name submitToken
			 * @description Handle token form submission
			 * @param {Object} payload Form submission payload
			 */
			async submitToken({ data }) {
				try {
					const response = await this.createUserLongLifeAccessToken(data);
					this.tokenResponse = response.data;
					this.$refs.tokenResponseDialog.open();
					// Emit event to reload credentials data
					EventBus.$emit('credentials-updated');
				} catch (error) {
					ElementTools.fireNotification(this.$el, 'error', 'Failed to create token');
				}
			},

			/**
			 * @name copyToClipboard
			 * @description Copy text to clipboard for paste
			 */
			copyToClipboard(text) {
				navigator.clipboard.writeText(text).then(
					() => {
						ElementTools.fireNotification(this.$el, 'success', `Copying access token to clipboard was successful`);
					},
					function (err) {
						console.error('Async: Could not copy text: ', err);
					}
				);
			}
		}
	};
</script>

<style lang="scss" scoped>
	.v-slide-group {
		::v-deep .v-slide-group__content {
			justify-content: center;
		}
	}
</style>
